import { Button } from '@/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import type { PhoneNumber } from 'honeygrid-types'
import { MoreHorizontal, Phone, Plus } from 'lucide-react'

interface PhoneNumbersTableProps {
	phoneNumbers: PhoneNumber[]
	onSelect: (phoneNumber: PhoneNumber) => void
	onAdd: () => void
	selectedId?: string
}

const getStatusBadge = (status: string) => {
	const baseClasses = "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
	switch (status) {
		case 'active':
			return `${baseClasses} bg-green-100 text-green-800`
		case 'pending':
			return `${baseClasses} bg-yellow-100 text-yellow-800`
		case 'released':
			return `${baseClasses} bg-gray-100 text-gray-800`
		default:
			return `${baseClasses} bg-gray-100 text-gray-800`
	}
}

export function PhoneNumbersTable({ phoneNumbers, onSelect, onAdd, selectedId }: PhoneNumbersTableProps) {
	return (
		<Table>
			<TableHeader className="border-t border-t-[#3A2E2F]/20 border-b border-b-[#3A2E2F] [&_tr]:!border-0">
				<TableRow>
					<TableHead>Phone Number</TableHead>
					<TableHead>Connection</TableHead>
					<TableHead>Status</TableHead>
					<TableHead className="w-12"></TableHead>
				</TableRow>
			</TableHeader>
			<TableBody className="[&_tr:last-child]:!border-b [&_tr]:!border-0">
				{phoneNumbers.length === 0 ? (
					<TableRow className="!border-b !border-b-[#3A2E2F]/20">
						<TableCell colSpan={4} className="h-32 p-0">
							<div className="flex flex-col items-center justify-center text-center space-y-3 bg-[#3A2E2F]/5 h-full py-8">
								<Phone className="h-8 w-8 text-foreground/60" />
								<p className="text-sm font-medium text-muted-foreground">No phone numbers yet</p>
								<Button variant="outline" size="sm" onClick={onAdd}>
									<Plus className="h-4 w-4 mr-2" />
									Add Phone Number
								</Button>
							</div>
						</TableCell>
					</TableRow>
				) : (
					phoneNumbers.map((phoneNumber) => (
						<TableRow
							key={phoneNumber.uuid}
							className={`group cursor-pointer hover:bg-muted/50 !border-b !border-b-[#3A2E2F]/20 ${
								phoneNumber.uuid === selectedId ? 'bg-muted' : ''
							}`}
							onClick={() => onSelect(phoneNumber)}
						>
							<TableCell className="font-medium">{phoneNumber.phone_number}</TableCell>
							<TableCell>Twilio</TableCell>
							<TableCell>
								<span className={getStatusBadge(phoneNumber.status)}>
									{phoneNumber.status}
								</span>
							</TableCell>
							<TableCell>
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button variant="ghost" size="sm" className="h-8 w-8 p-0" aria-label={`More actions for ${phoneNumber.phone_number}`}>
											<MoreHorizontal className="h-4 w-4" />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end">
										<DropdownMenuItem onClick={(e) => { e.stopPropagation(); onSelect(phoneNumber) }}>
											Edit
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							</TableCell>
						</TableRow>
					))
				)}
			</TableBody>
		</Table>
	)
}